/**
 * ImageEditorToolbar Component
 *
 * Drawing Tools für Canvas (Brush, Eraser, Rectangle, Magic Wand)
 * @accessibility WCAG 2.1 AA konform
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui';
import { Paintbrush, Eraser, Square, Sparkles, Undo, Trash2 } from 'lucide-react';
import type { CanvasTool } from '@/hooks/useCanvas';

interface ImageEditorToolbarProps {
  selectedTool: CanvasTool;
  onToolChange: (tool: CanvasTool) => void;
  brushSize: number;
  onBrushSizeChange: (size: number) => void;
  onUndo: () => void;
  onClear: () => void;
  disabled?: boolean;
}

export function ImageEditorToolbar({
  selectedTool,
  onToolChange,
  brushSize,
  onBrushSizeChange,
  onUndo,
  onClear,
  disabled = false,
}: ImageEditorToolbarProps) {
  const { t } = useTranslation();
  return (
    <div className="image-editor-toolbar flex items-center gap-4 p-4 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
      {/* Drawing Tools */}
      <div className="flex gap-2">
        <Button
          variant={selectedTool === 'brush' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => onToolChange('brush')}
          disabled={disabled}
          aria-label="Pinsel (B)"
          title="Pinsel (B)"
        >
          <Paintbrush className="h-4 w-4" />
        </Button>
        <Button
          variant={selectedTool === 'eraser' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => onToolChange('eraser')}
          disabled={disabled}
          aria-label="Radierer (E)"
          title="Radierer (E)"
        >
          <Eraser className="h-4 w-4" />
        </Button>
        <Button
          variant={selectedTool === 'rectangle' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => onToolChange('rectangle')}
          disabled={disabled}
          aria-label="Rechteck (R)"
          title="Rechteck (R)"
        >
          <Square className="h-4 w-4" />
        </Button>
        <Button
          variant={selectedTool === 'magic' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => onToolChange('magic')}
          disabled={disabled}
          aria-label="Zauberstab (W)"
          title="Zauberstab (W)"
        >
          <Sparkles className="h-4 w-4" />
        </Button>
      </div>

      {/* Divider */}
      <div className="h-8 w-px bg-neutral-300 dark:bg-neutral-700" />

      {/* Brush Settings */}
      <div className="flex items-center gap-4 flex-1">
        <div className="flex items-center gap-2 w-40">
          <label htmlFor="brush-size" className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-nowrap">
            {t('editor.brushSize')}
          </label>
          <input
            id="brush-size"
            type="range"
            min="1"
            max="100"
            value={brushSize}
            onChange={(e) => onBrushSizeChange(Number(e.target.value))}
            disabled={disabled}
            className="flex-1"
            aria-label={t('editor.brushSizeAria')}
          />
          <span className="text-xs text-neutral-500 dark:text-neutral-400 w-8 text-right">{brushSize}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={disabled}
          aria-label={t('editor.undoAria')}
          title={t('editor.undoTitle')}
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={disabled}
          aria-label={t('editor.clearMaskAria')}
          title={t('editor.clearMaskTitle')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
